import clsx from 'clsx'
import { useRef, useState } from 'react'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { ReactComponent as LinkIcon } from 'assets/icons/link-white.svg'
import { ReactComponent as CheckmarkIcon } from 'assets/icons/red-light-checkmark.svg'
import BottomArc from 'components/bottomArc/BottomArc'
import GridCardLayout from 'components/cardGridLayout/CardGridLayout'
import { Button } from 'components/common/button/Button'
import VideoModal from 'components/common/videoModal/VideoModal'
import { FrameworkWheelPage } from 'components/interactiveNavigation/FrameworkWheel'
import KeyTasks from 'components/keyTasks/KeyTasks'
import NavBar from 'components/navBar/NavBar'
import PageBanner from 'components/pageBanner/PageBanner'
import { ReturnToLandingButton } from 'components/returnToLandingButton/ReturnToLandingButton'
import { TopReturnToLandingButton } from 'components/topReturnToLandingButton/TopReturnToLandingButton'
import useHardNavigate from 'hooks/useHardNavigate'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './Assemble.module.scss'
import Tab1Img from './assets/images/tab-1-img.png'
import Tab2Img from './assets/images/tab-2-img.png'
import Tab3Img from './assets/images/tab-3-img.png'
import Tab4Img from './assets/images/tab-4-img.png'
import Tab5Img from './assets/images/tab-5-img.png'
import Tab6Img from './assets/images/tab-6-img.png'

export enum TabKeys {
  FALL_IN_LOVE_WITH_THE_TEAM = 'fall-in-love-with-the-team',
  FALL_IN_LOVE_WITH_THE_CHALLENGE = 'fall-in-love-with-the-challenge',
  FALL_IN_LOVE_WITH_THE_WAYS_OF_WORKING = 'fall-in-love-with-the-ways-of-working',
  MAP_OUT_RESOURCES_AND_MILESTONES = 'map-out-resources-and-milestones',
  EXPLORE_PAST_LEARNINGS = 'explore-past-learnings',
  MAP_AND_ENGAGE_WITH_KEY_STAKEHOLDERS = 'map-and-engage-with-key-stakeholders',
}

export interface NavTabType {
  key: TabKeys
  title: string
}

export const navTabs: NavTabType[] = [
  {
    key: TabKeys.FALL_IN_LOVE_WITH_THE_TEAM,
    title: 'Fall in love with the team',
  },
  {
    key: TabKeys.FALL_IN_LOVE_WITH_THE_WAYS_OF_WORKING,
    title: 'Fall in love with the ways of working',
  },
  {
    key: TabKeys.FALL_IN_LOVE_WITH_THE_CHALLENGE,
    title: 'Fall in love with the challenge',
  },

  {
    key: TabKeys.MAP_OUT_RESOURCES_AND_MILESTONES,
    title: 'Map out resources and milestones',
  },
  {
    key: TabKeys.EXPLORE_PAST_LEARNINGS,
    title: 'Explore past learnings',
  },
  {
    key: TabKeys.MAP_AND_ENGAGE_WITH_KEY_STAKEHOLDERS,
    title: 'Engage with key stakeholders',
  },
]

const Assemble = () => {
  const keyTasksRef = useRef<HTMLDivElement>(null)
  const keyPrinciplesRef = useRef<HTMLDivElement>(null)
  const keyComponentsRef = useRef<HTMLDivElement>(null)

  const [activeTab, setActiveTab] = useState<NavTabType['key']>(navTabs?.[0]?.key)

  const [play, setPlay] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [videoSrc, setVideoSrc] = useState<string>()

  const openVideoModal = (src: string) => {
    setVideoSrc(src)
    setPlay(true)
    setIsOpen(true)
  }

  const hardNavigate = useHardNavigate()

  const tabsData = [
    {
      key: TabKeys.FALL_IN_LOVE_WITH_THE_TEAM,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img src={Tab1Img} alt="Tab 1 Img" />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>Understand your team superpowers, team identity, and working principles</div>
            <div className={styles.items}>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Kick off with sponsors</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Team intro beyond titles</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Team's mission statement</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Project team-building activity</div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex justify-end">
              <Button
                variant="secondary"
                text="Open Mural"
                iconRight={<LinkIcon />}
                border
                className={styles.muralCTA}
                onClick={() => {
                  window.open('https://app.mural.co/t/general0591/template/1e69a495-9226-4b9a-8e92-a1fe72fffe8a')
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_1_CTA,
                  })
                }}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      key: TabKeys.FALL_IN_LOVE_WITH_THE_WAYS_OF_WORKING,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img
              alt="video-placeholder"
              src={Tab2Img}
              className="cursor-pointer"
              onClick={() => {
                openVideoModal(
                  'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/aa1f3d33-fdc1-4995-8fdc-6c5e11fc2ef4/public-download',
                )
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_2_VIDEO,
                })
              }}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              Define the full project plan and critical timelines, routines, and decision moments, surface any tensions
              early on, and start thinking about whether you will use a sprint-based approach.
            </div>
            <div className={styles.items}>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Project plan</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Routines, backlog, timings, stakeholders, decision moments</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Capability uplift & inspiration session</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Capabilities + additional investment mapout</div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex justify-end">
              <Button
                variant="secondary"
                text="Open Mural"
                iconRight={<LinkIcon />}
                border
                className={styles.muralCTA}
                onClick={() => {
                  window.open('https://app.mural.co/t/marketing6265/template/e9fbde00-79fc-4766-b9ae-192c3e700bec')
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_2_CTA,
                  })
                }}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      key: TabKeys.FALL_IN_LOVE_WITH_THE_CHALLENGE,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img src={Tab3Img} alt="Tab 3 Img" />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              Define in more depth the problem to solve, Jobs to be Done and what is in/ out of scope.
            </div>
            <div className={styles.items}>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Fall in Love with the Product through team tastings</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Product masterclasses</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>In-store immersions </div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Product trials</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Extended briefing with the R&D team</div>
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      key: TabKeys.MAP_OUT_RESOURCES_AND_MILESTONES,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img src={Tab4Img} alt="Tab 4 Img" />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>Assign the right people to get the work done</div>
            <div className={styles.items}>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Project Lead to guide project direction</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Core Team members to drive the work</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>
                  Extended Team members brought in to support specific project needs at key moments
                </div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>
                  Feedback Givers, such as participating OUs or markets for network projects to share notes and input
                </div>
              </div>

              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>
                  Project Sponsor to engage at critical points and make key decisions needed
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex justify-end">
              <Button
                variant="secondary"
                text="Open Mural"
                iconRight={<LinkIcon />}
                border
                className={styles.muralCTA}
                onClick={() => {
                  window.open(
                    'https://app.mural.co/t/general0591/m/general0591/1748251076498/551eb0371e7eeda5cef2af89f1329ac012886393?sender=u2736ef7e3dacd2ee85bd6031',
                  )
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_4_CTA,
                  })
                }}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      key: TabKeys.EXPLORE_PAST_LEARNINGS,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img
              src={Tab5Img}
              alt="Tab 5 Img"
              className="cursor-pointer"
              onClick={() => {
                openVideoModal(
                  'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/2a030acb-1e71-4c49-9a93-ba498e592865/public-download',
                )
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_5_VIDEO,
                })
              }}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              Explore Learnings of relevant projects to help boost your project success, learn what worked and what
              didn’t, and drive efficiency as we foster a continuous improvement culture where we learn and scale. Plan
              for a retrospective in 3 key steps:
            </div>
            <div className={styles.items}>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Celebrate what went well</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Discuss what didn't go as well or could have been better</div>
              </div>
              <div className={styles.itemRow}>
                <div className={styles.itemIcon}>
                  <CheckmarkIcon />
                </div>
                <div className={styles.itemTitle}>Determine how to make improvements </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex justify-end">
              <Button
                variant="secondary"
                text="Open Mural"
                iconRight={<LinkIcon />}
                border
                className={styles.muralCTA}
                onClick={() => {
                  window.open('https://app.mural.co/t/marketing6265/template/11f5cc6d-6e73-465f-ba36-852892d8fcdf')
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_5_CTA,
                  })
                }}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      key: TabKeys.MAP_AND_ENGAGE_WITH_KEY_STAKEHOLDERS,
      content: (
        <>
          <div className={styles.tabContentImage}>
            <img
              src={Tab6Img}
              alt="Tab 6 Img"
              className="cursor-pointer"
              onClick={() => {
                openVideoModal(
                  'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/ff6ca91d-3a07-4533-bf0e-dfcf4ae4b40f/public-download',
                )
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.ASSEMBLE.TABS.TAB_6_VIDEO,
                })
              }}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.title}>
              As you define your core team, extended team, and feedback givers, map your key stakeholders and their
              level of involvement so you can tailor communication and engagement strategies accordingly.
            </div>
            <div className={styles.items} />
          </div>
        </>
      ),
    },
  ]

  return (
    <div className={styles.Assemble}>
      <TopReturnToLandingButton />

      <PageBanner
        heading="Assemble"
        description="Can you feel the excitement? It's all coming together as you explore past learnings, assemble and onboard team members, and build the plan."
        showBubble
        bubbleText="Team! Assemble!"
        backgroundStyle={{ background: 'linear-gradient(180deg, #FBA7A7 0%, #F96B6C 100%)' }}
        showNavigationCircle
        page={FrameworkWheelPage.Assemble}
      />
      <NavBar
        items={[
          { key: 'key-tasks', title: 'Key Tasks', ref: keyTasksRef },
          { key: 'key-tasks', title: 'Key Principles', ref: keyPrinciplesRef },
          { key: 'key-tasks', title: 'Key Components & resources', ref: keyComponentsRef },
        ]}
      />
      <KeyTasks
        ref={keyTasksRef}
        color="redLight"
        tasks={[
          {
            title: 'Explore past learnings',
            description: 'Work smarter, not harder! Use past learnings as your starting point.',
            backgroundColor: '#FEE2E2',
          },
          {
            title: 'Assemble and onboard team',
            description:
              'Plan an experience that inspires the team about the challenge and helps everyone get to know each other.',
            backgroundColor: '#FEE2E2',
          },
          {
            title: 'Onboard the team',
            description:
              'Connect with participating markets, OUs, and key capabilities to get information and prepare the plan, including any necessary sprints. Finalize and sign off on Blueprint to map key resources and capabilities needed to get the work done.',
            backgroundColor: '#FEE2E2',
          },
        ]}
        inputs={[
          { text: 'LRP/ BP' },
          { text: 'BVA' },
          {
            text: 'Persistent Platform (including the product-centric approach)',
            link: process.env.REACT_APP_ENVIRONMENT_PERSISTENT_PLATFORM_URL,
          },
          { text: 'Past Learnings' },
        ]}
        outputs={[
          {
            text: 'Project Blueprint (offline)',
            link: 'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/e4cb7508-73ee-44b3-a065-64961b891225/public-download',
          },
          { text: 'Project Plan/ Backlog' },
          { text: 'Team & Ways of Working' },
        ]}
      />
      <div className="w-full bg-[#F4F4F4]">
        <GridCardLayout
          ref={keyPrinciplesRef}
          title="Key principles"
          titleDecoration="underline"
          cards={[
            {
              title: 'Assemble is essential',
              description: "Don't skip; instead adjust assemble to your needs.",
            },
            {
              title: 'Go slow to go faster',
              description:
                'Invest time upfront to connect with team and network to accelerate decision making and execution later.',
            },
            {
              title: 'Keep your team and sponsors close',
              description:
                'Start with your core team, then onboard the extended team and network. Engage your sponsor at the beginning and end of Assemble.',
            },
            {
              title: 'Aim for an experiential component',
              description:
                'Incorporate engaging activities and design immersive experiences. Leverage Assemble session to inject capabilities and refresh the Marketing Transformation agenda.',
            },
          ]}
        />

        <div>
          <VideoModal
            show={isOpen}
            setShow={setIsOpen}
            videoSrc={videoSrc || ''}
            setPlayVideo={play => {
              setPlay(play)
            }}
            playVideo={play}
          />
        </div>
        <div className={mainStyles.container}>
          <div className={styles.tabsSection} ref={keyComponentsRef}>
            <div className={styles.tabsTitle}>Components of a great Assemble</div>

            <div className={styles.tabsContainer}>
              <div className={styles.tabNavigation}>
                {navTabs.map(({ key, title }) => (
                  <div
                    key={key}
                    className={clsx(styles.tabNavItem, activeTab === key && styles.tabNavItemActive)}
                    onClick={() => setActiveTab(key)}
                  >
                    {title}
                  </div>
                ))}
              </div>
              <div className={styles.tabContent}>{tabsData.find(tab => tab.key === activeTab)?.content}</div>
            </div>
          </div>
        </div>
        <div className={clsx(mainStyles.container)}>
          <div className="flex justify-center">
            <ReturnToLandingButton variant="navigationControlHoverWhite" />
            <div className="mr-4" />
            <Button
              variant="navigationControlBlack"
              text="Go to Explore"
              iconRight={<ChevronRight />}
              border
              onClick={() => {
                hardNavigate('page-explore')
                trackAnalytics({
                  type: AnalyticsActionType.action,
                  payload: EVENTS.ACTION.ASSEMBLE.TO_EXPLORE,
                })
              }}
            />
          </div>
        </div>
        <BottomArc backgroundColor="#F4F4F4" />
      </div>
    </div>
  )
}

export default Assemble
